/**
 * Heuristic past-client scoring for import rows.
 * High confidence = structured closed-transaction signal.
 * Ambiguous rows (name+contact, no structured signal, has notes) are flagged for LLM.
 */

const PLACEHOLDER_NAMES = new Set([
  'seller', 'buyer', 'client', 'unknown', 'n/a', 'na', 'test', 'user', 'contact',
  'lead', 'prospect', 'none', 'null', 'undefined',
]);

const PAST_CLIENT_STAGE_PATTERNS = [
  /past\s*client/i,
  /\bclosed\b/i,
  /\bsold\b/i,
  /\bpurchased\b/i,
  /\bbought\b/i,
  /closed\s*won/i,
  /transaction\s*complete/i,
  /settled/i,
  /post[\s-]?close/i,
  /sphere/i,
  /\bsoi\b/i,
  /database/i,
  /past\s*customer/i,
  /former\s*client/i,
  // Active / current clients are people already working with the agent — nurture them too.
  /active\s*client/i,
  /current\s*client/i,
  /existing\s*client/i,
];

const LEAD_STAGE_PATTERNS = [
  /\blead\b/i,
  /new\s*lead/i,
  /prospect/i,
  /inquiry/i,
  /active\s*(buyer|seller)/i,
  /under\s*contract/i,
  /\bpending\b/i,
  /pre[\s-]?approv/i,
  /\bshowing/i,
  /nurture/i,
  /cold/i,
  /warm\s*lead/i,
  /hot\s*lead/i,
  /pipeline/i,
  /opportunity/i,
];

// Industry contacts / non-clients — never past clients regardless of other signals.
const NON_CLIENT_STAGE_PATTERNS = [
  /\bagents?\b/i,
  /\brealtors?\b/i,
  /\bbrokers?\b/i,
  /\bvendors?\b/i,
  /\blenders?\b/i,
  /\bloan\s*officers?\b/i,
  /\bmortgage\b/i,
  /\btitle\b/i,
  /\bescrow\b/i,
  /\binspectors?\b/i,
  /\bcontractors?\b/i,
  /\bappraisers?\b/i,
  /\bstagers?\b/i,
  /\bphotographers?\b/i,
  /\brecruit/i,
  /\bcompany\b/i,
  /\bbusiness\b/i,
  /\bother\b/i,
];

/**
 * Normalize CRM enum-style values ("PAST_CLIENT", "past-client") to plain words
 * so the word-boundary patterns above match them.
 * @param {unknown} value
 */
function normalizeStageValue(value) {
  return String(value ?? '')
    .replace(/[_\-./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {string|null|undefined} name
 * @returns {boolean}
 */
function looksLikeRealName(name) {
  if (!name) return false;
  const trimmed = String(name).trim();
  if (trimmed.length < 2) return false;
  if (PLACEHOLDER_NAMES.has(trimmed.toLowerCase())) return false;
  if (/^[\d\s\-().+]+$/.test(trimmed)) return false;
  if (trimmed.includes('@')) return false;
  if (/^[^a-zA-Z]+$/.test(trimmed)) return false;
  return true;
}

/**
 * @param {string} value
 * @param {string[]} extraValues from AI-inferred pastClientStageValues
 */
function matchesPastClientStage(value, extraValues = []) {
  if (!value) return false;
  const v = normalizeStageValue(value);
  if (!v) return false;
  const lower = v.toLowerCase();
  for (const extra of extraValues) {
    const e = normalizeStageValue(extra).toLowerCase();
    if (!e) continue;
    if (lower === e) return true;
    if (lower.includes(e)) return true;
  }
  return PAST_CLIENT_STAGE_PATTERNS.some((re) => re.test(v));
}

function matchesLeadStage(value) {
  if (!value) return false;
  return LEAD_STAGE_PATTERNS.some((re) => re.test(normalizeStageValue(value)));
}

function matchesNonClientStage(value) {
  if (!value) return false;
  return NON_CLIENT_STAGE_PATTERNS.some((re) => re.test(normalizeStageValue(value)));
}

function hasClosingDate(value) {
  if (!value?.trim()) return false;
  const v = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return true;
  if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(v)) return true;
  const d = new Date(v);
  return !Number.isNaN(d.getTime());
}

/**
 * Map a raw row array to a contact object using column mappings.
 * mappings: { [header]: systemField }
 * @param {string[]} headers
 * @param {string[]} row
 * @param {Record<string, string>} mappings
 */
export function mapRowToContact(headers, row, mappings) {
  const contact = {
    name: '',
    email: '',
    phone: '',
    location: '',
    stage: '',
    tags: '',
    notes: '',
    birthday: '',
    homeClosingDate: '',
    engagementLevel: '',
  };
  const addressParts = [];
  let firstName = '';
  let lastName = '';

  headers.forEach((header, index) => {
    const field = mappings[header];
    const value = String(row[index] ?? '').trim();
    if (!field || !value) return;

    switch (field) {
      case 'name':
        contact.name = value;
        break;
      case 'firstName':
        firstName = value;
        break;
      case 'lastName':
        lastName = value;
        break;
      case 'email':
        contact.email = value;
        break;
      case 'phone':
        contact.phone = value;
        break;
      case 'location':
        contact.location = value;
        break;
      case 'address':
      case 'city':
      case 'state':
      case 'zipCode':
        addressParts.push(value);
        break;
      case 'lifecycleStage':
        contact.stage = value;
        break;
      case 'tags':
        contact.tags = value;
        break;
      case 'notes':
        contact.notes = value;
        break;
      case 'birthday':
        contact.birthday = value;
        break;
      case 'homeClosingDate':
        contact.homeClosingDate = value;
        break;
      case 'engagementLevel':
        contact.engagementLevel = value;
        break;
      default:
        break;
    }
  });

  if (!contact.name && (firstName || lastName)) {
    contact.name = [firstName, lastName].filter(Boolean).join(' ').trim();
  }
  if (!contact.location && addressParts.length > 0) {
    contact.location = addressParts.join(', ');
  }

  return contact;
}

/**
 * Score a mapped contact for past-client likelihood.
 * @param {object} contact
 * @param {{ pastClientStageValues?: string[] }} options
 * @returns {{ confidence: 'high'|'medium'|'low'|'junk', reason: string, needsLlm: boolean, isPastClientHint: boolean|null }}
 */
export function scorePastClient(contact, options = {}) {
  const pastValues = options.pastClientStageValues || [];
  const hasName = looksLikeRealName(contact.name);
  const hasPhone = Boolean(contact.phone?.trim());
  const hasEmail = Boolean(contact.email?.trim());
  const hasContact = hasPhone || hasEmail;
  const hasClosing = hasClosingDate(contact.homeClosingDate);
  const stageIsPast = matchesPastClientStage(contact.stage, pastValues);
  const tagsArePast = matchesPastClientStage(contact.tags, pastValues);
  const stageIsLead = matchesLeadStage(contact.stage) && !stageIsPast;
  const stageIsNonClient = matchesNonClientStage(contact.stage) && !stageIsPast && !tagsArePast;
  const hasNotes = Boolean(contact.notes?.trim()) && contact.notes.trim().length >= 12;

  if (!hasName || !hasContact) {
    return {
      confidence: 'junk',
      reason: !hasName
        ? 'Missing or invalid name'
        : 'No phone or email — cannot contact',
      needsLlm: false,
      isPastClientHint: false,
    };
  }

  // Agents, vendors, lenders, etc. are industry contacts — never past clients,
  // even if a date column happened to be mapped as a closing date.
  if (stageIsNonClient) {
    return {
      confidence: 'low',
      reason: `Looks like an industry contact, not a client (“${contact.stage}”)`,
      needsLlm: false,
      isPastClientHint: false,
    };
  }

  if (hasClosing) {
    return {
      confidence: 'high',
      reason: 'Has a closing / purchase date',
      needsLlm: false,
      isPastClientHint: true,
    };
  }

  if (stageIsPast || tagsArePast) {
    return {
      confidence: 'high',
      reason: stageIsPast
        ? `Stage looks like past client (“${contact.stage}”)`
        : `Tags indicate past client (“${contact.tags}”)`,
      needsLlm: false,
      isPastClientHint: true,
    };
  }

  if (stageIsLead) {
    return {
      confidence: 'low',
      reason: `Looks like an active lead (“${contact.stage}”)`,
      needsLlm: false,
      isPastClientHint: false,
    };
  }

  // Ambiguous: contactable, no structured past-client or lead signal
  if (hasNotes) {
    return {
      confidence: 'medium',
      reason: 'No clear stage/closing date — checking notes',
      needsLlm: true,
      isPastClientHint: null,
    };
  }

  return {
    confidence: 'low',
    reason: 'No closing date, stage, or notes suggesting a past transaction',
    needsLlm: false,
    isPastClientHint: false,
  };
}

/**
 * Score all rows. Returns scored candidate objects.
 * @param {string[]} headers
 * @param {string[][]} rows
 * @param {Record<string, string>} mappings
 * @param {{ pastClientStageValues?: string[] }} options
 */
export function scoreImportRows(headers, rows, mappings, options = {}) {
  return rows.map((row, index) => {
    const contact = mapRowToContact(headers, row, mappings);
    const score = scorePastClient(contact, options);
    return {
      rowIndex: index,
      ...contact,
      confidence: score.confidence,
      reason: score.reason,
      needsLlm: score.needsLlm,
      isPastClientHint: score.isPastClientHint,
      lifecycleStage: score.isPastClientHint ? 'Past Client' : 'Lead',
      selected: score.confidence === 'high',
    };
  });
}
